import { TITLE_QUERY_CHANGED } from "./map-title-query-owner.js";
import {
  FISHYMAP_LAYER_FILTER_CHANGE_EVENT,
  FISHYMAP_LAYER_PRESENTATION_CHANGE_EVENT,
  FISHYMAP_WINDOW_LAYOUT_CHANGE_EVENT,
  mapLayerFilterChangeV1ToSignalPatch,
  mapLayerPresentationChangeV1ToSignalPatch,
  mapWindowLayoutChangeV1ToSignalPatch,
} from "./map-control-events.js";
import {
  FISHYMAP_BOOKMARKS_CHANGE_EVENT,
  FISHYMAP_CHARACTER_ACTIVITY_TARGET_CHANGE_EVENT,
  FISHYMAP_FOCUS_WORLD_POINT_REQUEST_EVENT,
  FISHYMAP_LOOT_CONDITION_OPTION_SELECT_EVENT,
  FISHYMAP_ZONE_INFO_TAB_CHANGE_EVENT,
  FISHYMAP_ZONE_LOOT_PROFILE_CHANGE_EVENT,
  bookmarksPatchFromPanelEvent,
  characterActivityTransitionFromPanelEvent,
  focusWorldPointPatchFromPanelEvent,
  lootConditionOptionPatchFromPanelEvent,
  zoneInfoTabPatchFromPanelEvent,
  zoneLootProfilePatchFromPanelEvent,
} from "./map-panel-event-contract.js";
import {
  FISHYMAP_QUERY_ACTIVATION_REQUESTED_EVENT,
  FISHYMAP_QUERY_EDITOR_STATE_REQUESTED_EVENT,
  FISHYMAP_QUERY_INPUT_RECEIPT_OWNER,
  FISHYMAP_QUERY_RESTORED_EFFECTS_REQUESTED_EVENT,
  FISHYMAP_TUTORIAL_HELP_WINDOW_REQUESTED_EVENT,
  mapQueryActivationRequestToSignalPatch,
  mapQueryEditorStateRequestToSignalPatch,
  mapQueryRestoredEffectsRequestToSignalPatch,
  mapTutorialHelpWindowRequestToSignalPatch,
  normalizeMapQueryActivationRequest,
} from "./map-ui-request-contract.js";
import {
  FISHING_CONTEST_SELECTION_CHANGE_EVENT,
  normalizeFishingContestSelectionProjection,
} from "../js/fishing-contest/contract.js";

export const MAP_SHELL_DOMAIN_EVENT_TYPES = Object.freeze([
  FISHYMAP_LAYER_FILTER_CHANGE_EVENT,
  FISHYMAP_LAYER_PRESENTATION_CHANGE_EVENT,
  FISHYMAP_WINDOW_LAYOUT_CHANGE_EVENT,
  FISHYMAP_QUERY_EDITOR_STATE_REQUESTED_EVENT,
  FISHYMAP_QUERY_ACTIVATION_REQUESTED_EVENT,
  FISHYMAP_QUERY_RESTORED_EFFECTS_REQUESTED_EVENT,
  FISHYMAP_TUTORIAL_HELP_WINDOW_REQUESTED_EVENT,
  FISHYMAP_FOCUS_WORLD_POINT_REQUEST_EVENT,
  FISHYMAP_BOOKMARKS_CHANGE_EVENT,
  FISHYMAP_ZONE_LOOT_PROFILE_CHANGE_EVENT,
  FISHYMAP_LOOT_CONDITION_OPTION_SELECT_EVENT,
  FISHYMAP_ZONE_INFO_TAB_CHANGE_EVENT,
  FISHYMAP_CHARACTER_ACTIVITY_TARGET_CHANGE_EVENT,
  FISHING_CONTEST_SELECTION_CHANGE_EVENT,
  TITLE_QUERY_CHANGED,
]);

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasPatchFields(value) {
  return isPlainObject(value) && Object.keys(value).length > 0;
}

export function createMapShellDomainEventController({
  shell,
  readProjection,
  dispatchPatch,
  requestBridgeInput,
  readTradeNpcCatalog = () => null,
  prepareCharacterActivityRefresh = () => () => {},
} = {}) {
  if (
    !shell
    || typeof shell.addEventListener !== "function"
    || typeof shell.removeEventListener !== "function"
  ) {
    throw new TypeError("Map shell domain events require an event-target shell");
  }
  if (typeof readProjection !== "function") {
    throw new TypeError("Map shell domain events require a projection reader");
  }
  if (typeof dispatchPatch !== "function") {
    throw new TypeError("Map shell domain events require a signal-patch owner");
  }
  if (typeof requestBridgeInput !== "function") {
    throw new TypeError("Map shell domain events require a bridge-input requester");
  }
  if (typeof readTradeNpcCatalog !== "function") {
    throw new TypeError("Map shell domain events require a Trade NPC catalog reader");
  }
  if (typeof prepareCharacterActivityRefresh !== "function") {
    throw new TypeError(
      "Map shell domain events require a Character activity refresh preparer",
    );
  }

  let started = false;

  function applyPatch(patch, receipt = null) {
    if (!hasPatchFields(patch)) {
      return false;
    }
    return dispatchPatch(patch, receipt) !== false;
  }

  function handle(eventType, detail) {
    switch (eventType) {
      case TITLE_QUERY_CHANGED:
        requestBridgeInput();
        return true;
      case FISHING_CONTEST_SELECTION_CHANGE_EVENT:
        normalizeFishingContestSelectionProjection(detail);
        requestBridgeInput();
        return true;
      case FISHYMAP_LAYER_FILTER_CHANGE_EVENT:
        return applyPatch(
          mapLayerFilterChangeV1ToSignalPatch(detail, readProjection()),
        );
      case FISHYMAP_LAYER_PRESENTATION_CHANGE_EVENT:
        return applyPatch(
          mapLayerPresentationChangeV1ToSignalPatch(detail, readProjection()),
        );
      case FISHYMAP_WINDOW_LAYOUT_CHANGE_EVENT:
        return applyPatch(
          mapWindowLayoutChangeV1ToSignalPatch(detail, readProjection()),
        );
      case FISHYMAP_QUERY_EDITOR_STATE_REQUESTED_EVENT:
        return applyPatch(mapQueryEditorStateRequestToSignalPatch(detail));
      case FISHYMAP_QUERY_ACTIVATION_REQUESTED_EVENT: {
        const request = normalizeMapQueryActivationRequest(detail);
        const patch = mapQueryActivationRequestToSignalPatch(
          request,
          readProjection(),
          { tradeNpcCatalog: readTradeNpcCatalog() },
        );
        return applyPatch(patch, {
          owner: FISHYMAP_QUERY_INPUT_RECEIPT_OWNER,
          revision: request.activationRevision,
        });
      }
      case FISHYMAP_QUERY_RESTORED_EFFECTS_REQUESTED_EVENT: {
        const patch = mapQueryRestoredEffectsRequestToSignalPatch(
          detail,
          readProjection(),
          { tradeNpcCatalog: readTradeNpcCatalog() },
        );
        return hasPatchFields(patch) ? applyPatch(patch) : true;
      }
      case FISHYMAP_TUTORIAL_HELP_WINDOW_REQUESTED_EVENT:
        return applyPatch(mapTutorialHelpWindowRequestToSignalPatch(detail));
      case FISHYMAP_FOCUS_WORLD_POINT_REQUEST_EVENT:
        return applyPatch(
          focusWorldPointPatchFromPanelEvent(detail, readProjection()),
        );
      case FISHYMAP_BOOKMARKS_CHANGE_EVENT:
        return applyPatch(
          bookmarksPatchFromPanelEvent(detail, readProjection()),
        );
      case FISHYMAP_ZONE_LOOT_PROFILE_CHANGE_EVENT:
        return applyPatch(
          zoneLootProfilePatchFromPanelEvent(detail, readProjection()),
        );
      case FISHYMAP_LOOT_CONDITION_OPTION_SELECT_EVENT:
        return applyPatch(
          lootConditionOptionPatchFromPanelEvent(detail, readProjection()),
        );
      case FISHYMAP_ZONE_INFO_TAB_CHANGE_EVENT:
        return applyPatch(
          zoneInfoTabPatchFromPanelEvent(detail, readProjection()),
        );
      case FISHYMAP_CHARACTER_ACTIVITY_TARGET_CHANGE_EVENT: {
        const transition = characterActivityTransitionFromPanelEvent(
          detail,
          readProjection(),
        );
        if (transition.refresh !== true) {
          return true;
        }
        const refreshCharacterActivity = prepareCharacterActivityRefresh();
        if (typeof refreshCharacterActivity !== "function") {
          return false;
        }
        if (!applyPatch(transition.patch)) {
          return false;
        }
        refreshCharacterActivity();
        return true;
      }
      default:
        throw new TypeError(
          `Unsupported FishyMap shell domain event: ${String(eventType)}`,
        );
    }
  }

  const listeners = new Map(
    MAP_SHELL_DOMAIN_EVENT_TYPES.map((eventType) => [
      eventType,
      (event) => {
        try {
          if (handle(eventType, event?.detail) !== true) {
            event?.preventDefault?.();
          }
        } catch (error) {
          event?.preventDefault?.();
          throw error;
        }
      },
    ]),
  );

  return Object.freeze({
    handle,
    start() {
      if (started) {
        return false;
      }
      const attached = [];
      try {
        for (const [eventType, listener] of listeners) {
          shell.addEventListener(eventType, listener);
          attached.push([eventType, listener]);
        }
      } catch (error) {
        for (const [eventType, listener] of attached) {
          shell.removeEventListener(eventType, listener);
        }
        throw error;
      }
      started = true;
      return true;
    },
    stop() {
      if (!started) {
        return false;
      }
      for (const [eventType, listener] of listeners) {
        shell.removeEventListener(eventType, listener);
      }
      started = false;
      return true;
    },
  });
}
